import { parseMetrics, type Metric } from "./metrics";
import type {
  KeyInventoryEntry,
  SandboxInfo,
  WorkspaceInfo,
} from "@timely-sandbox/core/types";

/**
 * 콘솔의 API 클라이언트.
 *
 * **자격증명을 다루지 않는다.** 인증은 Google 세션 쿠키뿐이고 그 쿠키는 `HttpOnly` 라
 * 이 코드가 읽지도 못한다 — same-origin 요청에 브라우저가 자동으로 실어 준다. 그래서
 * 여기 서명 코드도, 저장소도 없다.
 *
 * 응답 타입은 `@timely-sandbox/core` 에서 온다. 컨트롤러가 같은 타입을 쓰므로 wire
 * 계약이 바뀌면 **여기가 타입 에러로 잡힌다** (예전엔 문자열 템플릿이라 런타임까지 갔다).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly uri: string,
    body: string,
  ) {
    super(`HTTP ${status} ${uri}${body ? ` — ${body.slice(0, 180)}` : ""}`);
  }
}

async function get<T>(uri: string): Promise<{ data: T; identity: string | null }> {
  const res = await fetch(uri, { credentials: "same-origin" });
  if (!res.ok) throw new ApiError(res.status, uri, await res.text().catch(() => ""));
  return { data: (await res.json()) as T, identity: res.headers.get("x-tsbx-identity") };
}

interface Items<T> {
  items: T[];
}

export interface Snapshot {
  identity: string | null;
  sandboxes: SandboxInfo[];
  workspaces: WorkspaceInfo[];
  keys: KeyInventoryEntry[];
  /** 플릿 게이지 — 미claim spare 는 테넌트가 없어 목록 API 엔 안 보인다. */
  fleet: { total: string; byState: Array<{ state: string; value: number }> };
  /**
   * `/metrics` 전체. 개요 카드가 여기서 풀 적중률·회수 사유·요청 상태를 꺼낸다.
   *
   * 파싱된 배열을 그대로 들고 다니는 이유: 필요한 지표가 늘 때마다 Snapshot 타입을
   * 고치면, 서버가 지표를 하나 추가한 걸 클라가 못 보는 상태가 계속 생긴다.
   */
  metrics: Metric[];
}

/** 세션이 유효한지. 서명 없이 통하면 세션이 있는 것이다. */
export async function probeSession(): Promise<{ ok: boolean; identity: string | null }> {
  try {
    const res = await fetch("/v1/workspaces", { credentials: "same-origin" });
    return { ok: res.ok, identity: res.headers.get("x-tsbx-identity") };
  } catch {
    return { ok: false, identity: null };
  }
}

export async function logout(): Promise<void> {
  // POST 다 — GET 로그아웃은 남이 걸 수 있다 (SameSite=Lax 가 cross-site POST 를 막는다).
  await fetch("/auth/logout", { method: "POST", credentials: "same-origin" }).catch(() => {});
}

function parseGauges(prom: string, metric: string): Array<{ state: string; value: number }> {
  const out: Array<{ state: string; value: number }> = [];
  for (const line of prom.split("\n")) {
    if (!line.startsWith(metric + "{")) continue;
    const state = /state="([^"]*)"/.exec(line)?.[1] ?? "?";
    out.push({ state, value: Number(line.slice(line.lastIndexOf(" ") + 1)) });
  }
  return out;
}

/**
 * 한 번에 다 읽는다. **에러를 삼키지 않는다** — 예전에 catch 로 빈 목록을 넣었더니
 * 권한 문제(403)가 "플릿이 비었다"로 보였다. 고장이 정상처럼 보이는 게 가장 나쁘다.
 */
export async function loadSnapshot(): Promise<Snapshot> {
  const [prom, sandboxes, workspaces, keys] = await Promise.all([
    fetch("/metrics", { credentials: "same-origin" }).then((r) => (r.ok ? r.text() : "")),
    get<Items<SandboxInfo>>("/v1/sandboxes"),
    get<Items<WorkspaceInfo>>("/v1/workspaces"),
    // 발급 키 + 부트스트랩(env) 키. 후자가 빠지면 실제 트래픽을 나르는 pi-chat 키가
    // 목록에 없어 "키가 하나뿐" 으로 오해된다.
    get<Items<KeyInventoryEntry>>("/v1/keys"),
  ]);
  return {
    identity: sandboxes.identity,
    sandboxes: sandboxes.data.items,
    workspaces: workspaces.data.items,
    keys: keys.data.items,
    fleet: {
      total: /^tsbx_sandboxes_total (\d+)$/m.exec(prom)?.[1] ?? "?",
      byState: parseGauges(prom, "tsbx_sandboxes"),
    },
    metrics: parseMetrics(prom),
  };
}

/**
 * 삭제 둘. **에러를 삼키지 않는다** — 권한(403)·이미 없음(404)·서버 오류가 구분돼야
 * 패널이 사용자에게 무엇이 일어났는지 말할 수 있다.
 *
 * 세션으로 이걸 부를 수 있는 건 컨트롤러가 세션에 **이 두 경로만** 예외로 열어 뒀기
 * 때문이다 (http.ts `operatorMayDelete`). 다른 변경은 여전히 admin 키가 필요하다.
 */
async function del(uri: string): Promise<void> {
  const res = await fetch(uri, { method: "DELETE", credentials: "same-origin" });
  if (!res.ok) throw new ApiError(res.status, uri, await res.text().catch(() => ""));
}

/** 샌드박스와 그 디스크를 함께 지운다 — 이제 둘은 같은 것이다. */
export const deleteSandbox = (id: string): Promise<void> =>
  del(`/v1/sandboxes/${encodeURIComponent(id)}`);

/**
 * 발급 API. 세션에 열려 있는 범위는 **소비자용 워크스페이스 키까지**다 — admin 키 발급과
 * `?purge=1` 삭제는 컨트롤러가 세션에 주지 않는다 (`sessionMayWrite`). 그래서 여기 그
 * 두 함수가 없는 것이 의도다.
 */
async function post<T>(uri: string, body: unknown): Promise<T> {
  const res = await fetch(uri, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, uri, await res.text().catch(() => ""));
  return (await res.json()) as T;
}

export interface IssuedKey {
  keyId: string;
  /** 발급 응답에만 담긴다. 서버는 다시 내주지 않는다. */
  secret: string;
  workspace: string | null;
  expiresAt: number | null;
}

export const createWorkspace = (spec: {
  name: string;
  maxSandboxes?: number;
  maxVolumeGi?: number;
  note?: string;
}): Promise<WorkspaceInfo> => post<WorkspaceInfo>("/v1/workspaces", spec);

export const issueKey = (
  workspace: string,
  opts: { note?: string; ttlSec?: number },
): Promise<IssuedKey> =>
  post<IssuedKey>(`/v1/workspaces/${encodeURIComponent(workspace)}/keys`, opts);

export const revokeKey = (keyId: string): Promise<void> =>
  del(`/v1/keys/${encodeURIComponent(keyId)}`);

export const deleteWorkspace = (name: string): Promise<void> =>
  del(`/v1/workspaces/${encodeURIComponent(name)}`);
